// src/styles/AppStyles.ts
import styled, { keyframes } from 'styled-components';
import { COLORS } from './Colors';
import { Switch } from '@mui/material';

export const MOBILE_TOPBAR_HEIGHT = 56;

export const AppContainer = styled.div`
  font-family: Arial, sans-serif;
  display: flex;
  height: 100vh;
  height: 100dvh;
  position: relative;
  margin: 0;
  overflow: hidden;
  width: 100%;
`;

export const Sidebar = styled.div`
  width: 200px;
  background-color: ${COLORS.darkGray};
  display: flex;
  flex-direction: column;
  padding: 20px 0;
  justify-content: space-between;
  flex-shrink: 0;
`;


export const Content = styled.div<{ $compact?: boolean }>`
  flex: 1;
  min-width: 0;
  padding: ${({ $compact }) => ($compact ? '0' : '20px')};
  padding-top: ${({ $compact }) =>
    $compact
      ? `calc(${MOBILE_TOPBAR_HEIGHT}px + env(safe-area-inset-top, 0px))`
      : '20px'};
  background-color: ${COLORS.white};
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

export const NavScrim = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 1200;
`;

export const NavDrawer = styled.div<{ $open: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  height: 100dvh;
  z-index: 1201;
  display: flex;
  transform: translateX(${({ $open }) => ($open ? '0' : '-100%')});
  transition: transform 0.28s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: ${({ $open }) => ($open ? '4px 0 24px rgba(0, 0, 0, 0.25)' : 'none')};
  pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};
`;

export const Title = styled.h1`
  color: ${COLORS.teal};
  text-align: center;
  margin-bottom: 20px;
`;

export const AdminSidebar = styled.div`
  width: 200px;
  height: 100vh;
  background: #1a2533;
  z:
  display: flex;
  flex-direction: column;
  border-left: 1px solid #2c3e50;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.3);
  animation: slideIn 0.3s forwards;
  
  @keyframes slideIn {
    from {
      transform: translateX(-100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
`;


export const PageContainer = styled.div`
  padding: 5px;
  position: relative;
  flex: 1;
  min-height: 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media (max-width: 899px) {
    padding: 0;
  }
`;

export const AddButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  width: 50px;
  height: 50px;
  background-color: ${COLORS.teal};
  color: ${COLORS.white};
  border: none;
  border-radius: 50%;
  font-size: 24px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease, transform 0.2s ease;
  &:hover {
    background-color: ${COLORS.darkTeal};
    transform: scale(1.1);
  }

  @media (max-width: 899px) {
    position: fixed;
    top: auto;
    bottom: calc(20px + env(safe-area-inset-bottom, 0px));
    right: calc(16px + env(safe-area-inset-right, 0px));
    z-index: 1050;
    width: 56px;
    height: 56px;
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.28);
  }
`;

export const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background-color: ${COLORS.teal};
  color: ${COLORS.white};
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease, transform 0.2s ease;
  &:hover {
    transform: translateY(-2px);
  }
`;

export const BulkImportLink = styled.button`
  position: absolute;
  top: 80px;
  right: 20px;
  background: none;
  border: none;
  color: ${COLORS.teal};
  font-size: 0.9em;
  cursor: pointer;
  text-decoration: underline;
  &:hover {
    color: ${COLORS.darkTeal};
  }

  @media (max-width: 899px) {
    position: static;
    top: auto;
    right: auto;
    padding: 8px 0;
    min-height: 44px;
  }
`;

export const GridContainer = styled.div`
  height: 600px;
  width: 100%;
  border: 2px solid ${COLORS.teal};
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  margin-top: 20px;
`;

export const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 16px;
  overflow-y: auto;
  box-sizing: border-box;

  @media (max-width: 768px) {
    align-items: flex-end;
    padding: 0;
  }
`;

export const ModalContent = styled.div`
  width: 100%;
  max-width: 800px; /* Reduced from 1000px */
  background-color: ${COLORS.white};
  padding: 30px;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  border: 1px solid ${COLORS.lightGray};
  max-height: 85vh;
  overflow-y: auto;
  position: relative;
  box-sizing: border-box;

    scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none;  /* IE and Edge */
  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari */
  }


  @media (max-width: 768px) {
    padding: 20px 16px;
    max-height: 92dvh;
    border-radius: 16px 16px 0 0;
    max-width: 100%;
  }
`;



export const ModalHeader = styled.h2`
  text-align: center;
  margin: 0 0 30px 0;
  color: ${COLORS.darkGray};
  font-size: 1.5em;
`;

export const CloseButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  width: 30px;
  height: 30px;
  background-color: ${COLORS.white};
  border: 1px solid ${COLORS.gray};
  border-radius: 50%;
  font-size: 1.2em;
  color: ${COLORS.darkGray};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  &:hover {
    background-color: ${COLORS.lightGray};
    border-color: ${COLORS.darkGray};
    color: ${COLORS.teal};
  }
`;

export const RecordCount = styled.div`
  margin-bottom: 10px;
  font-size: 1.2em;
  color: ${COLORS.teal};
  font-weight: bold;
`;

export const ErrorMessage = styled.div`
  margin-bottom: 10px;
  font-size: 1.2em;
  color: ${COLORS.red};
  font-weight: bold;
`;

export const LoadingMessage = styled.div`
  margin-bottom: 10px;
  font-size: 1.2em;
  color: ${COLORS.gray};
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

export const Message = styled.div<{ type: 'success' | 'error' }>`
  padding: 12px 20px;
  margin: 15px 0;
  animation: ${fadeIn} 0.3s ease-out;
  border-radius: 6px;
  color: ${props => (props.type === 'success' ? COLORS.success : COLORS.red)};
  background-color: ${props => (props.type === 'success' ? `${COLORS.success}20` : `${COLORS.red}20`)};
  border: 1px solid ${props => (props.type === 'success' ? COLORS.success : COLORS.red)};
  font-weight: 500;
`;

export const TrackIconWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  width: 100%; // Take up full cell width
  height: 100%; // Take up full cell height
  & svg {
    color: ${COLORS.teal};
    font-size: 1.5em;
    transition: color 0.2s ease;
  }
  &:hover svg {
    color: ${COLORS.darkTeal};
  }
`;


export const checkboxStyle = {
  display: 'flex',
  alignItems: 'center',
  padding: '8px 12px',
  border: '1px solid rgba(0, 0, 0, 0.12)',
  borderRadius: '4px',
  minWidth: '180px',
  backgroundColor: 'transparent',
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
    cursor: 'pointer',
  },
  transition: 'background-color 0.2s ease',
};

export const checkboxInnerStyle = {
  padding: '2px',
  marginRight: '8px',
  '&.Mui-checked': {
    color: '#009688',
  },
};



export const FormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
`;

export const ImportWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

export const ImportContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
  width: 80%;
  max-width: 500px;

  @media (max-width: 640px) {
    width: 100%;
  }
`;

export const InstructionText = styled.p`
  color: ${COLORS.darkGray};
  font-size: 1em;
  text-align: center;
  line-height: 1.5;
`;

export const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 20px;
`;

export const Input = styled.input`
  padding: 14px;
  font-size: 1em;
  border: 1px solid ${COLORS.gray};
  border-radius: 6px;
  outline: none;
  &:focus {
    border-color: ${COLORS.teal};
    box-shadow: 0 0 4px ${COLORS.teal}50;
  }
`;

export const StyledFileInput = styled.input`
  padding: 12px;
  font-size: 1em;
  border: 1px solid ${COLORS.gray};
  border-radius: 6px;
  background-color: ${COLORS.white};
  cursor: pointer;
  &:hover {
    border-color: ${COLORS.teal};
  }
`;

export const ButtonGroup = styled.div`
  display: flex;
  gap: 15px;
  justify-content: center;
  margin-top: 25px;
  flex-wrap: wrap;

  @media (max-width: 640px) {
    flex-direction: column-reverse;

    & > button {
      width: 100%;
    }
  }
`;

export const SubmitButton = styled.button`
  padding: 12px 25px;
  background-color: ${COLORS.teal};
  color: ${COLORS.white};
  border: none;
  border-radius: 6px;
  font-size: 1em;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${COLORS.darkTeal};
    transform: translateY(-2px);
  }

  &:disabled {
    background-color: #ccc;
    color: #666;
    cursor: not-allowed;
    transform: none;
  }
`;


export const CancelButton = styled.button`
  padding: 12px 25px;
  background-color: ${COLORS.gray};
  color: ${COLORS.white};
  border: none;
  border-radius: 6px;
  font-size: 1em;
  font-weight: 600;
  cursor: pointer;
  &:hover {
    background-color: ${COLORS.darkGray};
    transform: translateY(-2px);
  }
`;

export const CheckboxGroupWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px 16px;
  margin: 20px 0;
`;

export const CheckboxItem = styled.div`
  display: flex;
  align-items: center;
  font-size: 14px;
`;

export const DeleteButton = styled.button`
  background-color: #f44336;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 5px 8px;
  cursor: pointer;
  font-size: 12px;
  &:hover {
    background-color: #d32f2f;
  }
`;

export const EditButton = styled.button`
  background: transparent;
  color: ${COLORS.teal};
  border: none;
  border-radius: 4px;
  padding: 5px 8px;
  cursor: pointer;
  font-size: 12px;
  &:hover {
    color: ${COLORS.darkGray};
    transform: translateY(-2px);
  }
`;

export const IconActionButton = styled.button<{ $variant?: 'primary' | 'danger' }>`
  background: transparent;
  border: none;
  border-radius: 999px;
  width: 30px;
  height: 30px;
  padding: 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${({ $variant }) => ($variant === 'danger' ? COLORS.red : COLORS.teal)};
  transition: background-color 0.15s ease, color 0.15s ease;

  & svg {
    width: 18px;
    height: 18px;
  }

  &:hover {
    background-color: ${({ $variant }) =>
      $variant === 'danger' ? `${COLORS.red}18` : `${COLORS.teal}18`};
    color: ${({ $variant }) => ($variant === 'danger' ? COLORS.red : COLORS.darkTeal)};
  }

  &:active {
    background-color: ${({ $variant }) =>
      $variant === 'danger' ? `${COLORS.red}22` : `${COLORS.teal}22`};
  }

  &:focus-visible {
    outline: 2px solid ${({ $variant }) => ($variant === 'danger' ? COLORS.red : COLORS.teal)};
    outline-offset: 2px;
  }
`;


export const DeleteConfirmContent = styled.div`
  padding: 20px;
  text-align: center;
`;

export const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-top: 20px;
  flex-wrap: wrap;

  @media (max-width: 640px) {
    flex-direction: column-reverse;

    & > button {
      width: 100%;
    }
  }
`;


export const DeleteConfirmButton = styled.button`
  padding: 12px 25px;
  background-color: ${COLORS.red};
  color: ${COLORS.white};
  border: none;
  border-radius: 6px;
  font-size: 1em;
  font-weight: 600;
  cursor: pointer;
  &:hover {
    background-color: ${COLORS.darkGray};
    transform: translateY(-2px);
  }
`;

export const TealSwitch = styled(Switch)(({ theme }) => ({
  '& .MuiSwitch-switchBase.Mui-checked': {
    color: 'teal',
    '& + .MuiSwitch-track': {
      backgroundColor: 'teal',
    },
  },
}));


export const StyledLabel = styled.label`
  font-weight: 600;
  color: ${COLORS.darkGray};
`;

export const StyledInput = styled.input`
  padding: 14px;
  font-size: 1em;
  border: 1px solid ${COLORS.gray};
  border-radius: 6px;
  outline: none;
  &:focus {
    border-color: ${COLORS.teal};
    box-shadow: 0 0 4px ${COLORS.teal}50;
  }
`;

export const StyledSelect = styled.select`
  padding: 14px;
  font-size: 1em;
  border: 1px solid ${COLORS.gray};
  border-radius: 6px;
  outline: none;
  &:focus {
    border-color: ${COLORS.teal};
    box-shadow: 0 0 4px ${COLORS.teal}50;
  }
`;